import React, { useState, useEffect } from 'react';
import { getAppLocale } from '../../utils/locale';
import { useMoney } from '../../hooks/useMoney';
import '../../styles/components/FormPage.css';
import { Input as FormInput, Select as FormSelect } from '../FormField';
import SearchableSelect from '../ui/SearchableSelect';
import RichTextEditor from '../ui/RichTextEditor';
import ConfirmDialog from '../ui/ConfirmDialog';
import { apiPost, apiPut, apiDelete, planningPath } from '../../services/PlanningApiClient';
import { refreshBudgetFromApi } from '../../utils/budgetStorage';
import { loadQuotes } from '../../services/VendorQuoteService';
import { loadVendorData } from '../../data/VendorLoader';
import type { ParsedVendor } from '../../data/vendorData';
import { useNotifications } from '../NotificationProvider';
import { useFormSaveEvent, useFormDeleteEvent, firstMissingField, useClearErrorsOnEdit } from './_shared';

interface BudgetCategoryFormPageProps {
  categoryId?: string;
  onSave: () => void;
  onCancel: () => void;
}

export const BudgetCategoryFormPage: React.FC<BudgetCategoryFormPageProps> = ({ categoryId, onSave, onCancel }) => {
  const money = useMoney();
  const { showSuccess, showError } = useNotifications();
  const [form, setForm] = useState({
    name: '', plannedAmount: 0, notes: '',
    eventId: '', vendor: '', priority: 'must-have' as string,
  });
  const [vendorOptions, setVendorOptions] = useState<ParsedVendor[]>([]);
  // Real dependent-data counts (budget line items + vendor quotes tied to
  // this category) — the DB has ON DELETE CASCADE from both budget_items
  // and vendor_quotes onto budget_categories, so a hard delete here
  // silently destroys those too. Surfaced in the confirm dialog so the
  // user knows what they're actually about to lose.
  const [linkedCounts, setLinkedCounts] = useState<{ items: number; quotes: number }>({ items: 0, quotes: 0 });
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    loadVendorData().then(setVendorOptions).catch(() => {});
  }, []);

  useEffect(() => {
    if (!categoryId) return;
    refreshBudgetFromApi().then((categories) => {
      const cat = categories.find((c) => String(c.id) === String(categoryId));
      if (cat) {
        setForm({
          name: cat.name || '',
          plannedAmount: cat.planned_amount || 0,
          notes: cat.notes || '',
          eventId: cat.event_id || '',
          vendor: cat.vendor || '',
          priority: cat.priority || 'must-have',
        });
        setLinkedCounts(prev => ({ ...prev, items: cat.vendors?.length || 0 }));
      }
    });
    loadQuotes()
      .then(quotes => {
        const count = quotes.filter(q => String(q.categoryId) === String(categoryId)).length;
        setLinkedCounts(prev => ({ ...prev, quotes: count }));
      })
      .catch(() => {});
  }, [categoryId]);

  const handleSave = async () => {
    const missing = firstMissingField([{ label: 'Name', value: form.name }]);
    if (missing) { showError(missing); return; }
    const payload = {
      name: form.name.trim(),
      planned_amount: form.plannedAmount,
      notes: form.notes || null,
      event_id: form.eventId || null,
      vendor: form.vendor || null,
      priority: form.priority || 'must-have',
    };
    // Awaited, so a rejected save throws to the shared hook (field errors under
    // the fields, plain-language toast) and the form is not reported as saved.
    if (categoryId) {
      await apiPut(planningPath(`/budget/categories/${categoryId}`), payload);
    } else {
      await apiPost(planningPath('/budget/categories'), payload);
    }
    onSave();
  };

  const fieldErrors = useFormSaveEvent(handleSave, { fallback: 'Failed to save the budget category.' });
  useClearErrorsOnEdit(
    { name: form.name, planned_amount: form.plannedAmount, notes: form.notes, event_id: form.eventId, vendor: form.vendor, priority: form.priority },
    fieldErrors,
  );
  // No `archived` column exists on budget_categories in ans-backend (verified against
  // db/schema/planning.ts and db/repositories/planning/budget.ts — updateBudgetCategory's
  // `allowed` column allowlist doesn't include it either), so an archive-first flow like
  // VendorTable's can't actually persist yet without a ans-backend schema/repo change. Until
  // that lands, this stays a real delete — but gated behind confirmation and a warning
  // about what else gets destroyed, since budget_items.category_id and
  // vendor_quotes.category_id both cascade-delete when the category goes.
  useFormDeleteEvent(() => {
    if (!categoryId) return;
    setConfirmDelete(true);
  });

  const handleConfirmDelete = () => {
    if (!categoryId) return;
    setConfirmDelete(false);
    apiDelete(planningPath(`/budget/categories/${categoryId}`))
      .then(() => showSuccess(`"${form.name}" deleted`, { persistent: true }))
      .catch(() => showError('Failed to delete budget category'));
    onSave();
  };

  const hasLinkedData = linkedCounts.items > 0 || linkedCounts.quotes > 0;
  const deleteWarningParts: string[] = [];
  if (linkedCounts.quotes > 0) deleteWarningParts.push(`${linkedCounts.quotes} vendor quote${linkedCounts.quotes === 1 ? '' : 's'}`);
  if (linkedCounts.items > 0) deleteWarningParts.push(`${linkedCounts.items} budget line item${linkedCounts.items === 1 ? '' : 's'}`);

  return (
    <div className="inline-form-page">
      <FormInput label="Category Name" type="text" value={form.name}
        error={fieldErrors.errorFor('name')}
        onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. Photography" required />
      <FormInput label={`Planned Amount (${money.unit})`} type="text" inputMode="numeric" value={form.plannedAmount > 0 ? form.plannedAmount.toLocaleString(getAppLocale()) : ''}
        error={fieldErrors.errorFor('planned_amount')}
        onChange={e => setForm({ ...form, plannedAmount: parseInt(e.target.value.replace(/,/g, ''), 10) || 0 })} placeholder="0" />
      <div className="form-row">
        <FormSelect label="Priority" value={form.priority}
          error={fieldErrors.errorFor('priority')}
          onChange={e => setForm({ ...form, priority: e.target.value })}
          options={[
            { value: 'must-have', label: 'Must Have' },
            { value: 'nice-to-have', label: 'Nice to Have' },
            { value: 'cut-if-needed', label: 'Cut if Needed' },
          ]} />
        <div className="form-group">
          <label>Primary Vendor</label>
          <SearchableSelect
            value={form.vendor}
            error={fieldErrors.errorFor('vendor')}
            onChange={value => setForm({ ...form, vendor: value })}
            options={vendorOptions.map(v => ({
              value: v.name,
              label: `${v.name}${v.services[0] ? ` (${v.services[0]})` : ''}`,
            }))}
            placeholder="Search vendors or type a name…"
          />
        </div>
      </div>
      <RichTextEditor label="Notes" value={form.notes}
        error={fieldErrors.errorFor('notes')}
        onChange={val => setForm({ ...form, notes: val })} rows={3}
        placeholder="Additional notes..." />
      <ConfirmDialog
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={handleConfirmDelete}
        title="Delete this budget category?"
        description={
          hasLinkedData
            ? `This category has ${deleteWarningParts.join(' and ')} linked to it. Deleting it will permanently delete those too. This cannot be undone.`
            : 'This cannot be undone.'
        }
        confirmLabel="Delete"
      />
    </div>
  );
};
